import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { http } from '../../config/HttpMethod';
import { ESP_ENDPOINTS } from '../../services/calculations/endpoints';

export interface User {
    userID?: number;
    firstName?: string;
    lastName?: string;
    email?: string;
}


interface AuthState {
    isLoggedIn: boolean | undefined;
    user: any;
    error: string | null;
    customers?: User[];
    customerEnabled: boolean;
    customerLoading?: boolean;
    customerCreated?: boolean;
    userLoading?: boolean;
}

export const initialState: AuthState = {
    isLoggedIn: false,
    user: null,
    error: null,
    customers: undefined,
    customerEnabled: false,
    customerLoading: true,
    customerCreated: false,
    userLoading: false,
};

export const login = createAsyncThunk('auth/login', async (ref: string | null) => {
    try {
        const response = await http.get(`${ESP_ENDPOINTS?.GET_ACCESS_TOKEN}${ref}`);
        if (response.data.success) {
            localStorage.setItem('Token', response.data.data.token);
            return response?.data?.data;
        }
        return Promise.reject(response?.data?.apiMessage);
    } catch (error) {
        return Promise.reject(error);
    }
});

export const postRefreshToken = createAsyncThunk('auth/refreshToken', async (params: any) => {
    try {
        const tokenPackage = localStorage.getItem('TokenPackage');
        let idToken = '';
        if (tokenPackage) {
            const parsedToken = JSON.parse(tokenPackage);
            idToken = parsedToken?.id_token
        }
        const response = await http.post(`${ESP_ENDPOINTS?.GET_REFRESH_TOKEN}`, params);
        if (response.data.success) {
            localStorage.setItem('Token', response.data.data.token);
            return response?.data?.data;
        }
        return Promise.reject(response?.data?.apiMessage);
    } catch (error) {
        return Promise.reject(error);
    }
});

export const autoLogin = createAsyncThunk('auth/autoLogin', async () => {
    try {
        const token = localStorage.getItem('Token');

        if (!token) {
            window.location.href = process.env.REACT_APP_LOGIN_URL!;
            return false;
        }

        const { data: isValid } = await http.get<boolean>(`${ESP_ENDPOINTS?.VALIDATE_LOGIN}`);

        if (!isValid) {
            window.location.href = process.env.REACT_APP_LOGIN_URL!;
            return false;
        }

        return true;
    } catch (error) {
        console.error('Auto-login failed:', error);
        return false;
    }
});


export const logout = createAsyncThunk('auth/logout', async () => {
    localStorage.clear();
    window.location.href = `${process.env.REACT_APP_LOGOUT_URL}`;
});

export const fetchUser = createAsyncThunk('auth/fetchUser', async () => {
    const response = await http.get(`${ESP_ENDPOINTS?.GET_USER_DETAILS}`);
    return response.data;
});

export const fetchUsermenu = createAsyncThunk('auth/fetchUserMenu', async (payload: any) => {
    const { Useremail, orgID } = payload;
    const params = { Useremail, orgID };
    const response = await http.get(`${ESP_ENDPOINTS?.GET_USER_MENU}`, { params });
    return response.data;
})

export const fetchAllCustomers = createAsyncThunk('auth/GetAllCustomers', async (companay: string) => {
    const response = await http.get(`${ESP_ENDPOINTS?.GET_ALL_CUSTOMERS}${companay}`);
    return response.data?.data ?? response.data;
})



const normalize = (str: string) =>
    str?.toLowerCase().replace(/[\s\-]/g, '');

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setIsLoggedIn(state, action) {
            state.isLoggedIn = action.payload;
        },
        setUser(state, action) {
            state.user = action.payload;
        },
        setCustomerCreated(state, action) {
            state.customerCreated = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(login.fulfilled, (state) => {
                state.isLoggedIn = true;
                state.error = null;
            })
            .addCase(login.rejected, (state: any, action: any) => {
                state.isLoggedIn = false;
                state.error = action?.error?.message;
            })
            .addCase(autoLogin.fulfilled, (state, action) => {
                state.isLoggedIn = action.payload;
            })
            .addCase(autoLogin.rejected, (state: any, action) => {
                state.isLoggedIn = false;
                state.error = action.payload;
            })
            .addCase(fetchUser.pending, (state) => {
                state.userLoading = true;
            })
            .addCase(fetchUser.fulfilled, (state, action) => {
                state.userLoading = false;
                state.user = action.payload;
                if (normalize(state.user?.userRole)?.includes(normalize('customer'))) {
                    state.customerEnabled = true;
                    state.customerLoading = false;
                } else {
                    state.customerEnabled = false;
                    state.customerLoading = false;
                }
            })
            .addCase(fetchAllCustomers.fulfilled, (state, action) => {
                state.customers = action.payload;
            })
            .addCase(fetchAllCustomers.rejected, (state, action: any) => {
                state.error = action?.error?.message;
            })
    },
});

export const { setIsLoggedIn, setUser, setCustomerCreated } = authSlice.actions;
export default authSlice.reducer;
